"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import { lazy, Suspense, useCallback, useEffect, useMemo } from "react";
import { toast } from "sonner";

import { TrackingScope } from "@/components/TrackingScope";
import { Spinner } from "@/components/ui/spinner";
import { buildListMembershipIndexes } from "@/contexts/build-list-membership-indexes";
import { listsFromItemsData } from "@/contexts/lists-from-items-data";
import { listsItemsStorageSnapshot } from "@/contexts/lists-items-storage-snapshot";
import type { TrackingScopeValue } from "@/contexts/trackingScopeContext";
import { useHashModal } from "@/hooks/use-hash-modal";
import { useTrackingScope } from "@/hooks/use-tracking-scope";
import { useViewerQueryState } from "@/hooks/use-viewer-query-state";
import type { AddToListsTarget } from "@/lib/add-to-lists-target";
import { consumeAddToListsIntent } from "@/lib/consume-add-to-lists-intent";
import { ensureGuestSession } from "@/lib/ensure-guest-session";
import { getListsItemsQueryOptions } from "@/routes/_app/lists/lists-items-query";
import { writeStoredQueryData } from "@/utils/query-storage";

import {
  ListsContext,
  type ItemQuery,
  type ListMeta,
  type ListsContextValue,
  type OpenAddToListsParams,
  type PreferredListType,
} from "./lists-context";

const EMPTY_SET = new Set<string>();
const EMPTY_ARRAY: ListMeta[] = [];
const ADD_TO_LISTS_MODAL_OWNER_ID = "lists-provider";
const AddToListsWizard = lazy(() =>
  import("@/components/lists/add-to-lists-action-wizard").then((module) => ({
    default: module.AddToListsActionWizard,
  })),
);
type AddToListsWizardState = {
  preferredListType: PreferredListType | null;
  target: AddToListsTarget;
  trackingScope: TrackingScopeValue | null;
};

function getListsItemsStorageKey(userId: string) {
  return `garimpa:lists-items:${userId}`;
}

function canResumeAddToListsIntent(pathname: string) {
  return pathname !== "/taste";
}

export function ListsProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const inheritedTrackingScope = useTrackingScope();
  const { session, viewerId, enabledWhenAuthenticated } = useViewerQueryState();
  const listItemsStorageKey = session ? getListsItemsStorageKey(session.user.id) : null;
  const storageSnapshot = listsItemsStorageSnapshot(listItemsStorageKey);

  const idsQuery = useQuery({
    ...getListsItemsQueryOptions({ viewerId, enabled: enabledWhenAuthenticated }),
    initialData: () => storageSnapshot.data,
    initialDataUpdatedAt: () => storageSnapshot.updatedAt,
  });

  useEffect(() => {
    if (!listItemsStorageKey || !idsQuery.data) return;
    writeStoredQueryData(listItemsStorageKey, idsQuery.data);
  }, [idsQuery.data, listItemsStorageKey]);

  const rawLists = listsFromItemsData(idsQuery.data);

  const allLists = useMemo<ListMeta[]>(
    () =>
      rawLists.map((l) => ({
        id: l.id,
        name: l.name,
        type: l.type,
        icon: l.icon,
        itemCount: l.releaseIds.length,
      })),
    [rawLists],
  );

  // Lookup maps: releaseId → Set<listId>, workId → Set<listId>
  const { releaseToListIds, workToListIds } = useMemo(() => {
    return buildListMembershipIndexes(rawLists);
  }, [rawLists]);

  const listById = useMemo(() => {
    const map = new Map<string, ListMeta>();
    for (const l of allLists) map.set(l.id, l);
    return map;
  }, [allLists]);

  const resolveListIds = useCallback(
    ({ workId, releaseId }: ItemQuery): Set<string> | undefined => {
      if (releaseId) {
        const set = releaseToListIds.get(releaseId);
        if (set) return set;
      }
      if (workId) {
        return workToListIds.get(workId);
      }
      return undefined;
    },
    [releaseToListIds, workToListIds],
  );

  const collectionListId = allLists.find((l) => l.type === "collection")?.id;
  const wishlistListId = allLists.find((l) => l.type === "wishlist")?.id;

  const getListsForItem = useCallback(
    (params: ItemQuery): ListMeta[] => {
      const listIds = resolveListIds(params);
      if (!listIds) return EMPTY_ARRAY;
      const result: ListMeta[] = [];
      for (const id of listIds) {
        const meta = listById.get(id);
        if (meta) result.push(meta);
      }
      return result;
    },
    [resolveListIds, listById],
  );

  const isInCollection = useCallback(
    (params: ItemQuery): boolean => {
      if (!collectionListId) return false;
      return resolveListIds(params)?.has(collectionListId) ?? false;
    },
    [resolveListIds, collectionListId],
  );

  const isInWishlist = useCallback(
    (params: ItemQuery): boolean => {
      if (!wishlistListId) return false;
      return resolveListIds(params)?.has(wishlistListId) ?? false;
    },
    [resolveListIds, wishlistListId],
  );

  const getListIdsForRelease = useCallback(
    (releaseId: string) => releaseToListIds.get(releaseId) ?? EMPTY_SET,
    [releaseToListIds],
  );

  const {
    close: closeHashModal,
    getState: getHashModalState,
    open: openHashModal,
  } = useHashModal();
  const wizardHashState = getHashModalState<AddToListsWizardState>("add-to-lists", {
    ownerId: ADD_TO_LISTS_MODAL_OWNER_ID,
  });
  const wizardOpen = wizardHashState.open;
  const wizardState = wizardHashState.open ? wizardHashState.payload : null;

  const closeWizard = useCallback(() => {
    closeHashModal("add-to-lists", { ownerId: ADD_TO_LISTS_MODAL_OWNER_ID });
  }, [closeHashModal]);

  useEffect(() => {
    if (!session?.user.onboardingComplete || !canResumeAddToListsIntent(pathname)) return;

    const intent = consumeAddToListsIntent();
    if (!intent) return;

    openHashModal(
      "add-to-lists",
      {
        preferredListType: intent.preferredListType ?? null,
        target: intent.target,
        trackingScope: null,
      },
      { ownerId: ADD_TO_LISTS_MODAL_OWNER_ID },
    );
  }, [openHashModal, pathname, session?.user.onboardingComplete]);

  const openAddToLists = useCallback(
    ({ target, preferredListType, trackingScope }: OpenAddToListsParams) => {
      const openWizard = () => {
        openHashModal(
          "add-to-lists",
          {
            preferredListType: preferredListType ?? null,
            target,
            trackingScope: trackingScope ?? null,
          },
          { ownerId: ADD_TO_LISTS_MODAL_OWNER_ID },
        );
      };

      if (session) {
        openWizard();
        return;
      }

      void ensureGuestSession(queryClient)
        .then((viewer) =>
          queryClient.fetchQuery({
            ...getListsItemsQueryOptions({ viewerId: viewer.userId }),
            staleTime: 0,
          }),
        )
        .then(openWizard)
        .catch((error: Error) => toast.error(error.message));
    },
    [openHashModal, queryClient, session],
  );

  const value = useMemo<ListsContextValue>(
    () => ({
      allLists,
      getListsForItem,
      isInCollection,
      isInWishlist,
      getListIdsForRelease,
      openAddToLists,
      isLoaded: idsQuery.isSuccess || idsQuery.isFetched,
      wizardOpen,
      closeWizard,
    }),
    [
      allLists,
      getListsForItem,
      isInCollection,
      isInWishlist,
      getListIdsForRelease,
      openAddToLists,
      idsQuery.isSuccess,
      idsQuery.isFetched,
      wizardOpen,
      closeWizard,
    ],
  );

  return (
    <ListsContext.Provider value={value}>
      {children}
      {wizardState && (
        <TrackingScope {...(wizardState.trackingScope ?? inheritedTrackingScope)}>
          <Suspense
            fallback={
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
                <Spinner />
              </div>
            }
          >
            <AddToListsWizard
              key={session?.user.id ?? "visitor"}
              target={wizardState.target}
              preferredListType={wizardState.preferredListType}
              open={wizardOpen}
              onClose={closeWizard}
            />
          </Suspense>
        </TrackingScope>
      )}
    </ListsContext.Provider>
  );
}
